import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore, Panel, Badge, Empty, Flash, useFlash, plural } from '../components/ui.jsx';
import {
  scheduleBetween, today, bookingsFor, memberById, checkIn, book,
  entitlementFor, staffById, getState, isSettleable,
} from '../data/store.js';
import { BOOKING_STATUS } from '../lib/booking.js';

const WINDOW_MIN = 45; // classes starting within this window are "now"

/**
 * The screen a front-desk tablet sits on all day: who is due in, one tap to
 * check them in, and a search that says plainly whether a walk-in may train.
 */
export default function FrontDesk() {
  useStore();
  const [q, setQ] = useState('');
  const [flash, setFlash] = useFlash();
  const state = getState();

  const t = today();
  const now = Date.now();
  const classes = scheduleBetween(t, t);

  const current = useMemo(() => classes.filter((occ) => {
    const start = new Date(occ.startsAt).getTime();
    return start > now - occ.durationMin * 60000 && start < now + WINDOW_MIN * 60000;
  }), [classes, now]);

  // Classes that are over stay on the page — people do turn up late, and the
  // desk still has to find them — but they sit below the live ones under their
  // own heading rather than presenting a row of Check in buttons for a class
  // that finished hours ago.
  //
  // `earlier` is drawn from the whole day rather than from the narrowed pool
  // on purpose: when a class is in progress the pool is just that class, and
  // filtering it would make this morning's roster unreachable exactly when
  // someone walks up asking about it.
  const [live, earlier] = useMemo(() => [
    (current.length ? current : classes).filter((occ) => !isSettleable(occ, now)),
    classes.filter((occ) => isSettleable(occ, now)),
  ], [current, classes, now]);

  const query = q.trim().toLowerCase();
  const matches = query
    ? state.members.filter((m) => [m.name, m.email, m.phone].filter(Boolean)
      .some((v) => v.toLowerCase().includes(query))).slice(0, 6)
    : [];

  return (
    <>
      <header className="topbar">
        <h1>Front desk</h1>
        <span className="sub">
          {current.length
            ? `${plural(current.length, 'class')} in progress or starting soon`
            : 'No class in the next 45 minutes'}
        </span>
        <div className="spacer" />
        <input
          type="search"
          placeholder="Look up a member…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ width: 280 }}
          aria-label="Look up a member"
        />
      </header>

      <div className="page">
        <Flash flash={flash} />

        {query && (
          <Panel title={`Lookup — ${plural(matches.length, 'match')}`} bodyless>
            {matches.length === 0 ? <Empty>No member by that name.</Empty> : (
              <table className="data">
                <thead>
                  <tr><th>Member</th><th>Eligibility</th><th className="right">Drop into</th></tr>
                </thead>
                <tbody>
                  {matches.map((m) => {
                    // Never offer to drop a walk-in into a class that has
                    // already finished.
                    const target = live[0];
                    const check = target ? entitlementFor(m.id, target, t) : null;
                    return (
                      <tr key={m.id}>
                        <td>
                          <Link to={`/members/${m.id}`}>{m.name}</Link>
                          <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{m.email}</div>
                        </td>
                        <td>
                          {!check ? <span style={{ color: 'var(--ink-3)' }}>No class to check</span>
                            : check.result.ok
                              ? <><Badge tone="good">clear</Badge> <span style={{ color: 'var(--ink-2)' }}>{check.plan?.name}</span></>
                              : <><Badge tone="bad">blocked</Badge> <span style={{ color: 'var(--ink-2)' }}>{check.result.reason}</span></>}
                        </td>
                        <td className="right">
                          <button
                            className="small primary"
                            disabled={!target || !check?.result.ok}
                            onClick={() => {
                              const r = book(m.id, target.id);
                              setFlash(r.ok
                                ? { tone: 'good', message: `${m.name} added to ${target.name}.` }
                                : { tone: 'bad', message: r.reason });
                            }}
                          >
                            {target ? target.name : '—'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Panel>
        )}

        {live.length === 0 && earlier.length === 0 && (
          <Panel><Empty>No classes today.</Empty></Panel>
        )}

        {live.map((occ) => (
          <ClassPanel key={occ.id} occurrence={occ} onFlash={setFlash} />
        ))}

        {earlier.length > 0 && (
          <>
            <h2 className="group-heading">Earlier today</h2>
            {earlier.map((occ) => (
              <ClassPanel key={occ.id} occurrence={occ} onFlash={setFlash} past />
            ))}
          </>
        )}
      </div>
    </>
  );
}

function ClassPanel({ occurrence: occ, onFlash, past }) {
  const seats = bookingsFor(occ.id);
  const expected = seats.filter(
    (b) => [BOOKING_STATUS.BOOKED, BOOKING_STATUS.ATTENDED].includes(b.status),
  );
  const here = expected.filter((b) => b.status === BOOKING_STATUS.ATTENDED).length;
  const waiting = seats.filter((b) => b.status === BOOKING_STATUS.WAITLISTED).length;
  const when = new Intl.DateTimeFormat('en-US', {
    timeZone: occ.timeZone, hour: 'numeric', minute: '2-digit',
  }).format(new Date(occ.startsAt));

  return (
    <Panel
      title={`${when} · ${occ.name}`}
      actions={(
        <span className="sub num">
          {here}/{expected.length} checked in
          {waiting > 0 && ` · ${waiting} waiting`}
          {' · '}{staffById(occ.staffId)?.name ?? 'Unassigned'}
        </span>
      )}
      bodyless
    >
      {expected.length === 0 ? <Empty>Nobody booked.</Empty> : (
        <table className="data">
          <thead>
            <tr>
              <th>Member</th>
              <th>Status</th>
              <th className="right">{past ? 'Late arrival' : 'Check in'}</th>
            </tr>
          </thead>
          <tbody>
            {expected.map((b) => {
              const member = memberById(b.memberId);
              const done = b.status === BOOKING_STATUS.ATTENDED;
              return (
                <tr key={b.id}>
                  <td>
                    {member ? <Link to={`/members/${member.id}`}>{member.name}</Link> : b.memberId}
                    {member && !member.waiverSignedAt && <> <Badge tone="warn">no waiver</Badge></>}
                  </td>
                  <td><Badge status={b.status} /></td>
                  <td className="right">
                    <button
                      // Not primary once the class is over: checking someone in
                      // is still possible, but it is no longer the obvious act.
                      className={done || past ? 'small' : 'small primary'}
                      disabled={done}
                      onClick={() => {
                        checkIn(b.id);
                        onFlash({ tone: 'good', message: `${member?.name ?? 'Member'} checked in.` });
                      }}
                    >
                      {done ? 'Here' : 'Check in'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
